/**
 * Printable barcode sheet for progress-tracker lines.
 * Encodes LGL|{discipline}|{rowId}|{labelKey} as Code128 with the human label under it.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import JsBarcode from 'jsbarcode';
import { authenticatedApiCall } from '../../utils/apiClient';
import {
  buildBarcodeValue,
  labelKeyFromRow,
} from '../../utils/trackerBarcode';

const sheetStyles = `
@media print {
  body * { visibility: hidden !important; }
  .lgl-barcode-print, .lgl-barcode-print * { visibility: visible !important; }
  .lgl-barcode-print {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
  .lgl-barcode-no-print { display: none !important; }
}
.lgl-barcode-print {
  font-family: "IBM Plex Sans", "Segoe UI", sans-serif;
  color: #111;
}
.lgl-barcode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  padding: 12px;
}
.lgl-barcode-card {
  border: 1px solid #d4d4d4;
  border-radius: 4px;
  padding: 12px 10px 8px;
  text-align: center;
  break-inside: avoid;
  page-break-inside: avoid;
  background: #fff;
}
.lgl-barcode-card svg { max-width: 100%; height: 56px; }
.lgl-barcode-label {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  word-break: break-word;
}
.lgl-barcode-meta {
  margin-top: 2px;
  font-size: 11px;
  color: #525252;
}
`;

const BarcodeSvg = ({ value }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        displayValue: false,
        margin: 0,
        height: 56,
        width: 1.4,
        background: '#ffffff',
        lineColor: '#111111',
      });
    } catch (err) {
      console.warn('Barcode render failed', err);
    }
  }, [value]);
  return <svg ref={ref} role="img" aria-label={value} />;
};

const BarcodePrintModal = ({
  open,
  onClose,
  disciplineId,
  projectId,
  projectDetails,
  rows = [],
  selectedRowIds,
}) => {
  const { instance, accounts } = useMsal();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedCount, setSavedCount] = useState(null);

  const selected = useMemo(() => {
    if (selectedRowIds && selectedRowIds.size > 0) {
      return rows.filter((row) => selectedRowIds.has(row.id));
    }
    return rows;
  }, [rows, selectedRowIds]);

  const labels = useMemo(() => (
    selected
      .map((row) => {
        const labelKey = labelKeyFromRow(disciplineId, row);
        if (!labelKey && row?.id == null) return null;
        return {
          id: row.id,
          labelKey: labelKey || `Item ${row.id}`,
          barcodeValue: buildBarcodeValue({
            discipline: disciplineId,
            rowId: row.id,
            labelKey: labelKey || `Item ${row.id}`,
          }),
        };
      })
      .filter(Boolean)
  ), [disciplineId, selected]);

  useEffect(() => {
    if (!open) return undefined;
    const style = document.createElement('style');
    style.setAttribute('data-lgl-barcode-print', '1');
    style.textContent = sheetStyles;
    document.head.appendChild(style);
    return () => style.remove();
  }, [open]);

  if (!open) return null;

  const persist = async () => {
    setSaving(true);
    setError('');
    setSavedCount(null);
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'post',
        `/tracker-barcodes/project/${projectId}/ensure`,
        {
          discipline: disciplineId,
          ids: labels.map((item) => item.id),
        }
      );
      setSavedCount(response.data?.count ?? labels.length);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save barcodes');
    } finally {
      setSaving(false);
    }
  };

  const print = async () => {
    await persist();
    window.print();
  };

  return (
    <div
      className="lgl-barcode-no-print"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        zIndex: 12000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(960px, 100%)',
          maxHeight: '90vh',
          overflow: 'auto',
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: '1px solid #e5e7eb',
        }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Print barcodes</div>
            <div style={{ fontSize: 13, color: '#525252' }}>
              {projectDetails?.job_number || ''} · {disciplineId} · {labels.length} label{labels.length === 1 ? '' : 's'}
              {selectedRowIds?.size > 0 ? ' (selected rows)' : ' (visible rows)'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={persist}
              disabled={saving || !labels.length}
              style={btnStyle}
            >
              {saving ? 'Saving…' : 'Save to database'}
            </button>
            <button
              type="button"
              onClick={print}
              disabled={!labels.length}
              style={{ ...btnStyle, background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' }}
            >
              Print
            </button>
            <button type="button" onClick={onClose} style={btnStyle}>Close</button>
          </div>
        </div>

        {error ? (
          <div style={{ padding: '8px 16px', color: '#b91c1c', fontSize: 13 }}>{error}</div>
        ) : null}
        {savedCount != null && !error ? (
          <div style={{ padding: '8px 16px', color: '#166534', fontSize: 13 }}>
            Saved {savedCount} barcode{savedCount === 1 ? '' : 's'} to the database.
          </div>
        ) : null}

        <div className="lgl-barcode-print">
          <div style={{ padding: '8px 16px 0', fontSize: 12, color: '#525252' }}>
            Scan the bars or the printed ISO / tag text in LGL Portal.
          </div>
          <div className="lgl-barcode-grid">
            {labels.map((item) => (
              <div className="lgl-barcode-card" key={`${item.id}-${item.barcodeValue}`}>
                <BarcodeSvg value={item.barcodeValue} />
                <div className="lgl-barcode-label">{item.labelKey}</div>
                <div className="lgl-barcode-meta">{disciplineId} · #{item.id}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const btnStyle = {
  border: '1px solid #d4d4d4',
  background: '#f8fafc',
  borderRadius: 6,
  padding: '7px 12px',
  fontSize: 13,
  cursor: 'pointer',
};

export default BarcodePrintModal;
